import React, { useEffect, useContext } from 'react';
import { Layout, Spin } from 'antd';
import { userHooks, commonHooks } from 'hooks';
import routes from 'routes';
import { IRoute } from 'interfaces';
import AppSider from './AppSider';
import AppHeader from './AppHeader';
import AppContent from './AppContent';
import './AppLayout.scss';
import { StoreContext } from 'contexts';
import localStorageConstants from 'constants/localStorage';

const { useWindowDimensions, useLocalStorage } = commonHooks;

const { SIDER_COLLAPSED } = localStorageConstants;
let autoCollapseSider = true;

/**
 * This container is for lifting-up the `AppContent` to the parent node,
 * so we can avoid unnecessary re-calculation when resizing window
 * */
const AppLayoutContainer: React.FC<{ filteredNavigation: IRoute[] }> = ({
  children,
  filteredNavigation,
}) => {
  const { isTabletView } = useWindowDimensions();
  const [siderCollapsed, setSiderCollapsed] = useLocalStorage<boolean>(
    SIDER_COLLAPSED,
    false,
    (stored: string) => stored === 'true',
    (value: boolean) => (value ? 'true' : 'false')
  );
  const { fetchingContent } = useContext(StoreContext);

  const toggleSider = () => {
    autoCollapseSider = false;
    setSiderCollapsed(collapsed => !collapsed);
  };

  useEffect(() => {
    if (autoCollapseSider && isTabletView) {
      setSiderCollapsed(isTabletView);
    }
  }, [isTabletView]);

  return (
    <Layout className="app-layout">
      <AppSider
        filteredNavigation={filteredNavigation}
        collapsed={siderCollapsed}
      />
      <Layout>
        <AppHeader onClickSiderIcon={toggleSider} />
        {fetchingContent ? <Spin className="app-spin" /> : children}
      </Layout>
    </Layout>
  );
};

const AppLayout: React.FC = () => {
  const { filteredRoutes, filteredNavigation } = userHooks.useAuthorizationData(
    routes
  );

  return (
    <AppLayoutContainer filteredNavigation={filteredNavigation}>
      <AppContent filteredRoutes={filteredRoutes} />
    </AppLayoutContainer>
  );
};

export default AppLayout;
